import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert, Anchor, Badge, Button, Group, Pagination, Paper, Select, Stack, Table, Text, TextInput, Tooltip,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import {
  IconAlertCircle, IconInfoCircle, IconLock, IconRefresh, IconSearch, IconX,
} from '@tabler/icons-react'
import { EmptyState, PageTitle, SectionCard, Skeleton, StatusBadge } from '@lighthouse/design'
import { useEtlJobCatalog } from '../../hooks/useEtlJobs'
import { useExecutions } from '../../hooks/useExecutions'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../../utils/format'
import { hangfireJobUrl } from '../../utils/hangfireUrl'
import type { ExecutionListItem } from '../../types/executions'
import {
  PAGE_SIZE, RUN_OUTCOMES, WINDOW_HEADER_HINT, durationText, emptyListCopy, entityCountSummary, errorCopy,
  isFiltered, listView, moduleOptions, readListFilters, recordsLoadedCaveatFor, runCountText, runOutcomePresentation,
  staleRuleText, targetText,
  toExecutionQuery, windowText, withFilter,
} from './executionFormat'
import type { OutcomePresentation } from './executionFormat'

/** The status Select's "no filter" entry. Not '' — Mantine treats an empty option value as no selection. */
const ALL = 'all'

const STATUS_OPTIONS = [
  { value: ALL, label: 'All' },
  ...RUN_OUTCOMES.map((outcome) => ({ value: outcome, label: runOutcomePresentation(outcome).label })),
]

/**
 * Execution History — the list. Every ETL run (Etl.EtlRun, 365 days) as one
 * row, newest first.
 *
 * The run grain, not the entity grain: the ETL Explorer lists entity runs, and
 * a run that halted before any entity started has no row there at all. Here it
 * is one row with its outcome, its window and its entity counts, and the
 * detail links each entity row into the Explorer.
 *
 * EVERY FILTER IS IN THE URL AND IS APPLIED SERVER-SIDE. The URL, so
 * `/execution-history?status=stale` is a link the Overview (and a person) can
 * send; server-side, because a page is 25 of up to thousands of runs and the
 * running/stale split must come from the server's one stale rule. Nothing on
 * this page filters, counts or re-derives an outcome client-side.
 */
export function ExecutionList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = readListFilters(searchParams)
  const filtered = isFiltered(filters)

  const { data, isLoading, isError, error, isFetching, isPlaceholderData, refetch } =
    useExecutions(toExecutionQuery(filters))
  const catalog = useEtlJobCatalog()

  const setFilter = (key: Parameters<typeof withFilter>[1], value: string | null) =>
    setSearchParams(withFilter(searchParams, key, value))

  const modules = moduleOptions(catalog.data?.jobs, filters.module)
  const view = listView({ isError, isLoading, isPlaceholderData, data })
  const totalPages = data ? Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE)) : 1

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Ship's Log — every ETL run as one row: its outcome, its window and each entity it loaded. Kept 365 days.">
        Execution History
      </PageTitle>

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={filters.status ?? ALL}
            onChange={(value) => setFilter('status', value === ALL ? null : value)}
            allowDeselect={false}
            w={210}
          />
          <Select
            label="Module"
            placeholder={catalog.isLoading ? 'Loading modules…' : 'Any'}
            description="Matches whole-module loads and entity loads of that module"
            data={modules}
            value={filters.module}
            onChange={(value) => setFilter('module', value)}
            clearable
            searchable
            nothingFoundMessage={catalog.isError ? 'The job catalogue did not load' : 'No such module'}
            w={260}
          />
          <EntityFilter applied={filters.entity} onApply={(value) => setFilter('entity', value)} />
          <DateInput
            label="Started from"
            placeholder="Any day"
            clearable
            value={filters.from}
            onChange={(value) => setFilter('from', value)}
            w={160}
          />
          <DateInput
            label="Started to (inclusive)"
            placeholder="Any day"
            clearable
            value={filters.to}
            onChange={(value) => setFilter('to', value)}
            w={170}
          />
          <Button
            variant="subtle"
            leftSection={<IconX size={16} />}
            onClick={() => setSearchParams(new URLSearchParams())}
            disabled={!filtered && filters.page === 1 && !filters.unknownStatus}
          >
            Clear
          </Button>
          <Tooltip label="Re-read run history now (it also refreshes every 30 seconds)">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => void refetch()}
              loading={isFetching}
              ml="auto"
            >
              Refresh
            </Button>
          </Tooltip>
        </Group>
      </Paper>

      {filters.unknownStatus && (
        <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" title="A filter in the link was ignored">
          <Text size="sm">
            &ldquo;{filters.unknownStatus}&rdquo; is not a run outcome, so the list is not filtered by status.
          </Text>
        </Alert>
      )}

      {filters.status === 'stale' && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          <Text size="sm">
            Stale runs are kept until someone closes them; nothing trims a run that never finished.
            {data && <> {staleRuleText(data.staleRule)}.</>}
          </Text>
        </Alert>
      )}

      {view === 'error' ? (
        <ExecutionErrorPanel error={error} />
      ) : view === 'loading' || !data ? (
        <ListSkeleton />
      ) : view === 'empty' ? (
        <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <EmptyState
            icon={filtered ? <IconSearch size={28} stroke={1.5} /> : <IconInfoCircle size={28} stroke={1.5} />}
            {...emptyListCopy(filtered)}
          />
        </Paper>
      ) : view === 'past-end' ? (
        <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <EmptyState
            title={`Page ${filters.page} is past the end`}
            message={`${runCountText(data.totalCount, filtered)}, on ${totalPages} page${totalPages === 1 ? '' : 's'}.`}
            action={<Button variant="light" onClick={() => setFilter('page', null)}>Go to the first page</Button>}
          />
        </Paper>
      ) : (
        <SectionCard
          // The previous filter's total is not this filter's; say so until this one answers.
          title={isPlaceholderData ? 'Updating…' : runCountText(data.totalCount, filtered)}
          subtitle={`${staleRuleText(data.staleRule)}. Newest first.`}
          flush
        >
          {/* The previous page stays on screen while the next loads; dimmed, so it is not read as the answer. */}
          <div style={{ opacity: isPlaceholderData ? 0.55 : 1, transition: 'opacity 120ms' }} aria-busy={isPlaceholderData}>
            <Table.ScrollContainer minWidth={1100}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Run key</Table.Th>
                    <Table.Th>Target</Table.Th>
                    <Table.Th>
                      <Tooltip label={WINDOW_HEADER_HINT} multiline w={280}>
                        <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>Window</span>
                      </Tooltip>
                    </Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Started</Table.Th>
                    <Table.Th>Duration</Table.Th>
                    <Table.Th>Entities</Table.Th>
                    <Table.Th ta="right">Records loaded</Table.Th>
                    <Table.Th>Hangfire</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.items.map((run) => (
                    <RunRow key={run.runId} run={run} onOpen={() => navigate(`/execution-history/${run.runId}`)} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </div>

          <Group justify="space-between" p="md">
            <Text size="sm" c="dimmed">
              {isPlaceholderData
                ? 'Updating…'
                : <>Page {filters.page} of {totalPages} · {runCountText(data.totalCount, filtered)}</>}
            </Text>
            {totalPages > 1 && (
              <Pagination
                value={filters.page}
                total={totalPages}
                onChange={(page) => setFilter('page', String(page))}
              />
            )}
          </Group>
        </SectionCard>
      )}
    </Stack>
  )
}

/** One run. The run key is the keyboard affordance — a `<tr>` takes no focus; the row click is for the mouse. */
function RunRow({ run, onOpen }: { run: ExecutionListItem; onOpen: () => void }) {
  return (
    <Table.Tr style={{ cursor: 'pointer' }} onClick={onOpen}>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        <Anchor
          component={Link}
          to={`/execution-history/${run.runId}`}
          size="sm"
          ff="monospace"
          onClick={(event) => event.stopPropagation()}
        >
          {run.runKey}
        </Anchor>
      </Table.Td>
      <Table.Td>{targetText(run.requested)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{windowText(run.requested)}</Table.Td>
      <Table.Td><OutcomeBadge presentation={runOutcomePresentation(run.outcome)} /></Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>{whenLabel(run.startedAt)}</Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{durationText(run)}</Table.Td>
      <Table.Td>
        <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>{entityCountSummary(run)}</Text>
      </Table.Td>
      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        <RecordsLoaded value={run.recordsLoaded} caveat={recordsLoadedCaveatFor(run)} />
      </Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        <HangfireJob id={run.hangfireJobId} />
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * The entity filter. Applied on Enter, not per keystroke: every change is a
 * server query, and a half-typed name matches nothing.
 */
function EntityFilter({ applied, onApply }: { applied: string | null; onApply: (value: string | null) => void }) {
  const [draft, setDraft] = useState(applied ?? '')
  // When the URL's value moves under the draft (Back/Forward, Clear), the draft follows it. Adjusted
  // during render rather than by remounting, which would drop focus from the box on every Enter.
  const [seen, setSeen] = useState(applied)
  if (seen !== applied) {
    setSeen(applied)
    setDraft(applied ?? '')
  }
  const pending = draft.trim() !== (applied ?? '')
  return (
    <TextInput
      label="Entity"
      placeholder="e.g. SupplierInvoice"
      description={pending ? 'Press Enter to apply' : 'Exact name; press Enter to apply'}
      value={draft}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onApply(draft.trim() || null)
      }}
      w={220}
    />
  )
}

/** A run or entity outcome. A null role is a plain gray badge — Cancelled, Skipped, Not started, or unknown. */
export function OutcomeBadge({ presentation }: { presentation: OutcomePresentation }) {
  return presentation.role
    ? <StatusBadge status={presentation.role} label={presentation.label} />
    : <Badge color="gray" variant="light" radius="sm">{presentation.label}</Badge>
}

/** Records loaded, with the served caveat beside it when there is one — never silently dropped. */
export function RecordsLoaded({ value, caveat }: { value: number; caveat: string | null }) {
  if (!caveat) return <>{exactNumber(value)}</>
  return (
    <Tooltip label={caveat} multiline w={320}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'help' }}>
        {exactNumber(value)}
        <IconInfoCircle size={14} aria-label={caveat} style={{ color: 'var(--color-text-secondary, inherit)' }} />
      </span>
    </Tooltip>
  )
}

/**
 * The Hangfire job that created the run: a link when this build knows where
 * the dashboard is, the id as text when it does not (never a relative href),
 * a dash when none was recorded.
 */
export function HangfireJob({ id }: { id: string | null }) {
  if (!id) return <>{NOT_MEASURED}</>
  const url = hangfireJobUrl(id)
  if (!url) {
    return (
      <Tooltip label="Hangfire dashboard address not configured for this build">
        <Text size="sm" ff="monospace" component="span">#{id}</Text>
      </Tooltip>
    )
  }
  return (
    <Anchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      ff="monospace"
      onClick={(event) => event.stopPropagation()}
    >
      #{id}
    </Anchor>
  )
}

/**
 * A request that failed, by kind. A 403 is neutral (a lock, not red) — a
 * refusal is a state to explain; everything else is an error panel with the
 * first line of what the server said.
 */
export function ExecutionErrorPanel({ error }: { error: unknown }) {
  const kind = errorKind(error)
  const copy = errorCopy(kind)
  const said = errorSummary(error)
  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      <div role={kind === 'forbidden' ? 'status' : 'alert'}>
        <EmptyState
          icon={kind === 'forbidden'
            ? <IconLock size={28} stroke={1.5} />
            : <IconAlertCircle size={28} stroke={1.5} color="var(--mantine-color-red-6)" />}
          title={copy.title}
          message={<>{copy.message}{said && <> The server says: {said}</>}</>}
        />
      </div>
    </Paper>
  )
}

/** Loading: the table's shape, so it is not mistaken for an empty history. */
function ListSkeleton() {
  return (
    <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }} aria-busy>
      <Stack gap="sm">
        <Skeleton height={20} width="40%" />
        {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} height={28} />)}
      </Stack>
    </Paper>
  )
}
